import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// icons
import {
  AddCircleOutline,
  Fullscreen,
  FullscreenExitOutlined,
  FullscreenOutlined,
  SyncOutlined,
} from "@mui/icons-material";
import { Button, Divider, IconButton, Tooltip } from "@mui/material";
import { isArray, isEmpty, size } from "lodash";
import isNil from "lodash/isNil";
import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import Flx from "../../../../components/layout/Flx";
import FilterTabSwitcher from "../../../../components/navigation/FilterTabSwitcher";
import LoanUnderwritingConditionsTable from "../../../../components/tables/LoanUnderwritingConditionsTable";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import { loanFetchedDataUwRequirementsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import SearchInput from "../../../../components/inputs/SearchInput";
import { blue, grey } from "@mui/material/colors";

const LoanDrilldownConditionsTab = ({ value }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const [quickFilter, setQuickFilter] = useState(null);
  const { loading, getLoanUnderwritingRequirements } = useChecklistRouteHook();
  const [tab, setTab] = useState("All");
  const [fullWidth, setFullWidth] = useState(false);
  const tabs = useMemo(
    () => buildStatusFilterTabs(uwRequirements),
    [uwRequirements],
  );

  const filteredConditions = useMemo(() => {
    if (tab === "All") {
      return uwRequirements;
    }
    return uwRequirements?.filter((c) => c.status === tab);
  }, [tab, uwRequirements]);

  const onAdd = useCallback(() => {
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION));
    dispatch(sidebarSetStateValue(null));
  }, []);

  const onEditClick = useCallback((data) => {
    dispatch(sidebarTypeSet(loanDrilldownSidebarTypes.ADD_EDIT_LOAN_CONDITION));
    dispatch(sidebarSetStateValue(data));
  }, []);

  useEffect(() => {
    if (
      isNil(uwRequirements) ||
      (!isArray(uwRequirements) && !isNil(loan?._id))
    ) {
      getLoanUnderwritingRequirements({
        dealId: loan?._id,
        onSuccessFn: (data) => {
          dispatch(loanFetchedDataUwRequirementsSet(data));
        },
      });
    }
  }, [uwRequirements]);

  return (
    <PrimaryContentCard
      maxWidth={fullWidth ? false : "xl"}
      loading={loading}
      cardSx={{
        borderTopRightRadius: fullWidth ? 0 : "8px",
        borderTopLeftRadius: fullWidth ? 0 : "8px",
      }}
      titleEndContent={<RefreshConditionsButton />}
      subTitle={"Click on any of the following condition cards to edit."}
      title={
        isNil(uwRequirements)
          ? "Loan Conditions"
          : `Loan Conditions (${size(uwRequirements)})`
      }
      endContent={
        <Flx ac g={1}>
          <Tooltip
            title={fullWidth ? "Exit Fullscreen" : "Fullscreen"}
            placement="top"
          >
            <IconButton
              sx={{ background: fullWidth ? blue[50] : "transparent" }}
              size="small"
              color={fullWidth ? "primary" : "default"}
              onClick={() => setFullWidth((v) => !v)}
            >
              {fullWidth ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            </IconButton>
          </Tooltip>
          <Button startIcon={<AddCircleOutline />} onClick={onAdd}>
            Add Condition
          </Button>
        </Flx>
      }
    >
      <Divider sx={{ mt: 0.5, mb: 4, borderWidth: "1.5px" }} />
      {isEmpty(uwRequirements) ? (
        <Flx sx={{ py: 2 }}>
          <Txt sx={{ fontStyle: "italic" }}>
            No conditions created yet for this loan.
          </Txt>
        </Flx>
      ) : (
        <Flx column>
          <Flx g={1} fw jb ac wrap sx={{ mb: 2 }}>
            <FilterTabSwitcher
              value={tab}
              fontSize={11}
              onChange={setTab}
              tabs={tabs}
            />
            <SearchInput value={quickFilter} onChange={setQuickFilter} />
          </Flx>
          <LoanUnderwritingConditionsTable
            rowData={filteredConditions}
            quickFilter={quickFilter}
            onRowClicked={onEditClick}
          />
        </Flx>
      )}
    </PrimaryContentCard>
  );
};
const RefreshConditionsButton = ({ children }) => {
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const dispatch = useDispatch();
  const onClick = () => {
    dispatch(loanFetchedDataUwRequirementsSet(null));
  };

  return (
    <Tooltip title="Refresh Conditions" placement="top">
      <IconButton
        size="small"
        loading={isNil(uwRequirements)}
        onClick={onClick}
      >
        <SyncOutlined />
      </IconButton>
    </Tooltip>
  );
};
const buildStatusFilterTabs = (conditions = []) => {
  if (isNil(conditions)) return [];
  const counts = {};
  conditions.forEach((c) => {
    const status = c.status || "Unknown";
    counts[status] = (counts[status] || 0) + 1;
  });

  const tabs = [{ label: "All", value: "All", count: conditions.length }];
  Object.entries(counts).forEach(([status, count]) => {
    tabs.push({ label: status, value: status, count });
  });

  return tabs;
};

export default LoanDrilldownConditionsTab;
